import React from 'react';
import PropTypes from 'prop-types';

import { CONTACTS } from '@department-of-veterans-affairs/component-library/contacts';
import recordEvent from 'platform/monitoring/record-event';
import { VA_FORM_IDS } from 'platform/forms/constants';
import readableList from 'platform/forms-system/src/js/utilities/data/readableList';

const READABLE_IDENTIFIER_MAPPING = {
  participantId: 'Participant ID',
  birlsId: 'BIRLS ID',
  ssn: 'Social Security Number',
  birthDate: 'Date of Birth',
  edipi: 'EDIPI',
};

const filterMissingIdentifiers = form526RequiredIdentifiers => {
  return Object.keys(form526RequiredIdentifiers).filter(
    idName => form526RequiredIdentifiers[idName] === false,
  );
};

const formatMissingIdentifiers = missingIdentifiers => {
  const readableIdentifiers = missingIdentifiers.map(
    idName => READABLE_IDENTIFIER_MAPPING[idName],
  );

  return readableList(readableIdentifiers);
};

const Alert = ({ children, title }) => (
  <div className="vads-l-grid-container vads-u-padding-left--0 vads-u-padding-bottom--5">
    <div className="usa-content">
      <h1>{title}</h1>
      <va-alert status="error" uswds>
        {children}
      </va-alert>
    </div>
  </div>
);

Alert.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
};

const displayContent = (title, form526RequiredIdentifiers, formId) => {
  const missingIdentifiers = filterMissingIdentifiers(
    form526RequiredIdentifiers,
  );

  recordEvent({
    event: 'visible-alert-box',
    'alert-box-type': 'warning',
    'alert-box-heading': title,
    'error-key': `missing_526_identifiers_${missingIdentifiers.join('_')}`,
    'form-id': formId,
    'alert-box-full-width': false,
    'alert-box-background-only': false,
    'alert-box-closeable': false,
  });

  const formattedIdentifiers = formatMissingIdentifiers(missingIdentifiers);

  return (
    <>
      <h2
        slot="headline"
        className="vads-u-display--inline-block vads-u-font-size--h3 vads-u-margin-top--0"
      >
        We need more information for your application
      </h2>

      <p className="vads-u-font-size--base">
        We’re missing your {formattedIdentifiers}. We need this information
        before you can file for disability compensation with VA Form 21-526EZ.
      </p>

      <p>
        Call us at <va-telephone contact="8662793677" /> (
        <va-telephone contact={CONTACTS['711']} tty />) to submit a request to
        update your information. We’re here Monday through Friday, 8:00 a.m. to
        8:00 p.m. ET. Tell the representative you need help getting this missing
        information added to your VA records so you can file VA Form 21-526EZ.
      </p>
    </>
  );
};

export const Missing526Identifiers = ({
  title,
  form526RequiredIdentifiers,
  formId = VA_FORM_IDS.FORM_21_526EZ,
}) => {
  const alertContent = displayContent(
    title,
    form526RequiredIdentifiers,
    formId,
  );

  return <Alert title={title}>{alertContent}</Alert>;
};

Missing526Identifiers.propTypes = {
  title: PropTypes.string.isRequired,
  form526RequiredIdentifiers: PropTypes.arrayOf(
    PropTypes.shape({
      participantId: PropTypes.bool.isRequired,
      birlsId: PropTypes.bool.isRequired,
      ssn: PropTypes.bool.isRequired,
      birthDate: PropTypes.bool.isRequired,
      edipi: PropTypes.bool.isRequired,
    }),
  ),
  formId: PropTypes.string,
};
